import React, { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import ParentForm, { Parent } from '../components/ParentForm';
import TableFilter from '../components/TableFilter';
import ActionButtons from '../components/ActionButtons';
import ReportModal from '../components/ReportModal';

const ParentsPage: React.FC = () => {
  const [parents, setParents] = useState<Parent[]>([]);
  const [filteredParents, setFilteredParents] = useState<Parent[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [editingParent, setEditingParent] = useState<Parent | null>(null);

  // Modal de Relatórios
  const [reportModal, setReportModal] = useState<{ isOpen: boolean; rowId?: number; isRowReport: boolean }>({
    isOpen: false,
    isRowReport: false
  });

  const fetchParents = useCallback(async () => {
    try {
      const response = await api.get('/parents/');
      setParents(response.data);
      setFilteredParents(response.data);
    } catch (error) {
      console.error("Error fetching parents", error);
    }
  }, []);

  useEffect(() => {
    fetchParents();
  }, [fetchParents]);

  const handleFilter = (filters: { [key: string]: string }) => {
    let result = parents;

    if (filters.name) {
      result = result.filter(p => p.name.toLowerCase().includes(filters.name.toLowerCase()));
    }
    if (filters.email) {
      result = result.filter(p => p.email.toLowerCase().includes(filters.email.toLowerCase()));
    }
    if (filters.phone) {
      result = result.filter(p => p.phone.includes(filters.phone));
    }

    setFilteredParents(result);
  };

  const handleSuccess = () => {
    setShowForm(false);
    setEditingParent(null);
    fetchParents();
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingParent(null);
  };

  const handleEdit = (parent: Parent) => {
    setEditingParent(parent);
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este responsável?')) {
      try {
        await api.delete(`/parents/${id}/`);
        fetchParents();
      } catch (error) {
        console.error("Error deleting parent", error);
        alert('Erro ao excluir responsável.');
      }
    }
  };

  const openGeneralReports = () => setReportModal({ isOpen: true, isRowReport: false });
  const openRowReport = (id: number) => setReportModal({ isOpen: true, rowId: id, isRowReport: true });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h1>Pais / Responsáveis</h1>
          <p style={{ color: 'var(--text-secondary)', margin: 0 }}>Gerencie os responsáveis e suas turmas</p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button className="btn btn-secondary" onClick={openGeneralReports}>Relatórios de Pais</button>
          {!showForm && (
            <button className="btn" onClick={() => setShowForm(true)}>
              + Novo Responsável
            </button>
          )}
        </div>
      </div>

      {showForm ? (
        <div style={{ marginBottom: '30px' }}>
          <ParentForm
            onSuccess={handleSuccess}
            onCancel={handleCancel}
            parentToEdit={editingParent}
          />
        </div>
      ) : (
        <TableFilter
          filters={[
            { key: 'name', label: 'Nome' },
            { key: 'email', label: 'Email' },
            { key: 'phone', label: 'Telefone' }
          ]}
          onFilter={handleFilter}
        />
      )}

      {reportModal.isOpen && (
        <ReportModal
          isOpen={reportModal.isOpen}
          onClose={() => setReportModal({ ...reportModal, isOpen: false })}
          category="Parent"
          rowId={reportModal.rowId}
          isRowReport={reportModal.isRowReport}
        />
      )}

      <div className="card" style={{ overflowX: 'auto' }}>
        <table>
          <thead>
            <tr>
              <th>Nome</th>
              <th>Email</th>
              <th>Telefone</th>
              <th>Turmas</th>
              <th>Status</th>
              <th style={{ textAlign: 'right' }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredParents.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ textAlign: 'center', padding: '30px', color: 'var(--text-light)' }}>
                  Nenhum responsável encontrado.
                </td>
              </tr>
            ) : (
              filteredParents.map((parent) => (
                <tr key={parent.id}>
                  <td style={{ fontWeight: '500' }}>{parent.name}</td>
                  <td>{parent.email}</td>
                  <td>{parent.phone}</td>
                  <td>
                    {parent.school_classes && parent.school_classes.length > 0 ? (
                      parent.school_classes.map((cls) => (
                        <span key={cls.id} className="badge badge-info" style={{ marginRight: '5px' }}>
                          {cls.name}
                        </span>
                      ))
                    ) : (
                      <span style={{ color: 'var(--text-light)' }}>-</span>
                    )}
                  </td>
                  <td>
                    {parent.is_active !== false ? (
                      <span className="badge badge-success">Ativo</span>
                    ) : (
                      <span className="badge badge-danger">Inativo</span>
                    )}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <ActionButtons
                      onEdit={() => handleEdit(parent)}
                      onDelete={() => parent.id && handleDelete(parent.id)}
                      onReport={() => parent.id && openRowReport(parent.id)}
                      deletePermission="delete_parent"
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ParentsPage;
